import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft } from "react-feather";
import toast from "react-hot-toast";
import { useCreatePettyCashRefillMutation, useGetPettyCashFundsQuery, useUploadPettyCashRefillAttachmentMutation } from "../../pettyCashApi";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useClaims } from "../../../auth/useClaims";
import { paths } from "../../../../routes/paths";
import { useDefaultFormBranch } from "../../../branchFilter/useDefaultFormBranch";

const money = (n) => Number(n || 0).toFixed(2);

const RefillCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { branches: callerBranchCodes, isAllBranches } = useClaims();
  const { data: branches = [] } = useGetBranchesQuery();
  const { data: funds = [] } = useGetPettyCashFundsQuery({ isActive: true });
  const [createRefill, { isLoading }] = useCreatePettyCashRefillMutation();
  const [uploadAttachment] = useUploadPettyCashRefillAttachmentMutation();

  const availableBranches = isAllBranches
    ? branches
    : branches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  // Arrived from a specific fund's row on the Funds page, which is now the way in -
  // that row already said which fund, so don't make anyone say it again.
  const presetBranchId = searchParams.get("branchId");

  const defaultBranchId = useDefaultFormBranch(availableBranches);
  const [branchId, setBranchId] = useState(
    presetBranchId || (defaultBranchId)
  );
  // A new record starts on the header branch (useDefaultFormBranch, 2026-10-02); the
  // branch list loads async, so it is filled in once known unless already chosen.
  useEffect(() => {
    if (!branchId && defaultBranchId !== "") setBranchId(defaultBranchId);
  }, [defaultBranchId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [requestedAmount, setRequestedAmount] = useState("");
  const [reason, setReason] = useState("");
  // Optional supporting document (Batch195): attached right after the request is created -
  // the create call is JSON and the file goes to its own endpoint.
  const [file, setFile] = useState(null);

  const targetFund = funds.find((f) => String(f.branchId) === String(branchId)) || null;

  const handleSave = async () => {
    if (availableBranches.length > 1 && !branchId) return toast.error("Branch is required");
    if (!requestedAmount || Number(requestedAmount) <= 0) return toast.error("Enter a valid amount");
    if (file && file.size > 2 * 1024 * 1024) return toast.error("The document must be 2MB or smaller");
    try {
      const refill = await createRefill({
        branchId: branchId ? Number(branchId) : undefined,
        requestedAmount: Number(requestedAmount),
        reason: reason || undefined,
      }).unwrap();
      toast.success("Refill request submitted");
      if (file) {
        try {
          await uploadAttachment({ recordGuid: refill.recordGuid, file }).unwrap();
        } catch (uploadError) {
          // The request exists either way - say so, and let it be attached from the detail page.
          toast.error(uploadError?.message || "The request was submitted but the document could not be attached - add it from the request page.");
        }
      }
      navigate(paths.pettyCashRefillDetail(refill.recordGuid), { replace: true });
    } catch (error) {
      toast.error(error?.message || "Could not submit the refill request");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.pettyCashFunds} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to Funds
              </Link>
              <h4>Request a Refill</h4>
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Refill Details</h5>

            {/* Which fund this actually credits, named before it is submitted - the
                fund is resolved server-side from the branch (one fund per branch),
                so without this the requester never sees where the money is going. */}
            {targetFund && (
              <div className="alert alert-info py-2">
                Crediting <strong>{targetFund.fundName}</strong> ({targetFund.branchCode}) - balance now{" "}
                {money(targetFund.currentBalance)} {targetFund.currencyCode}, minimum{" "}
                {money(targetFund.minimumBalance)}. The amount is added to the fund when the request is
                approved.
              </div>
            )}

            <div className="row">
              {availableBranches.length > 1 && (
                <div className="col-md-4 mb-3">
                  <label className="form-label">Branch</label>
                  <select className="form-control" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                    <option value="">--Select--</option>
                    {availableBranches.map((b) => (
                      <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="col-md-4 mb-3">
                <label className="form-label">Requested Amount</label>
                <input type="number" className="form-control" value={requestedAmount} onChange={(e) => setRequestedAmount(e.target.value)} />
              </div>
              <div className="col-md-12 mb-3">
                <label className="form-label">Reason (optional)</label>
                <textarea className="form-control" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Supporting document (optional)</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,application/pdf"
                  className="form-control"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                <div className="form-text">JPEG, PNG or PDF, up to 2MB - for example the bank slip or the cash count.</div>
              </div>
            </div>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isLoading}>Submit</button>
              <button type="button" className="btn btn-secondary" onClick={() => navigate(paths.pettyCashRefills)}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefillCreate;
